import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { GitConnectionKind, GitHubRepository } from "~/api/gitTypes";
import { useApi } from "~/api/provider";
import {
  useCreateGitConnection,
  useGitHubAppStatus,
  useGitHubInstallations,
  useGitHubRepositories,
  useProjects,
  useStartGitHubInstall,
} from "~/api/queries";
import { kindHint, kindLabel, repositoryName } from "~/features/git/gitLabels";
import { Select } from "~/features/git/parts";
import {
  type ConnectPrefill,
  findInstallationFor,
  httpsRemote,
  isGitHubRemote,
  parseRemote,
  resolvePrefillProject,
} from "~/features/git/prefill";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";

/**
 * The kinds a person can pick, in the order the server recommends them (the two that store
 * nothing first). `deploy-key` is accepted by the vocabulary but refused at ingest — history is
 * read over the API — so it is not offered here: a form must not offer what cannot work.
 */
const OFFERED_KINDS: GitConnectionKind[] = ["mount", "public", "app", "pat"];

interface FormState {
  projectId: string;
  kind: GitConnectionKind;
  remoteUrl: string;
  defaultBranch: string;
  pathPrefix: string;
  token: string;
  installationId: string;
}

export interface GitConnectionDialogProps {
  /** Pre-scoped to a project; `lockProject` hides the scope picker (the project's own dialog). */
  projectId?: string | null;
  lockProject?: boolean;
  /** Whether this host can seal a token — from `git.connections.list`. */
  secretsAvailable: boolean;
  /** Opened on the way back from GitHub: start on the GitHub App kind with this installation. */
  initialInstallationId?: string | null;
  /**
   * "Connect this repo to Docit" from VS Code (2026-09-28): the remote, branch and project to
   * preselect. A GitHub remote goes the GitHub App way when an installation covers it; anything
   * else starts on the public kind with the https URL.
   */
  prefill?: ConnectPrefill | null;
  onClose: () => void;
}

/** The kind a prefill starts on: the app for a GitHub remote, public for any other host. */
function prefillKind(prefill: ConnectPrefill | null | undefined): GitConnectionKind | null {
  if (!prefill) {
    return null;
  }
  if (isGitHubRemote(prefill.remote)) {
    return "app";
  }
  return parseRemote(prefill.remote) ? "public" : "mount";
}

/**
 * Connect a repository to the workspace or to a project. The safety rules are the server's
 * (no key ⇒ no token; a token that can write is refused); this form only stops asking for
 * what the host cannot keep.
 */
export function GitConnectionDialog({
  projectId,
  lockProject,
  secretsAvailable,
  initialInstallationId,
  prefill,
  onClose,
}: GitConnectionDialogProps) {
  const { t } = useTranslation();
  const { data: projects } = useProjects();
  const create = useCreateGitConnection();
  const startKind = prefillKind(prefill);
  const [form, setForm] = useState<FormState>({
    projectId: projectId ?? "",
    kind: initialInstallationId ? "app" : (startKind ?? "mount"),
    remoteUrl:
      startKind === "public"
        ? (httpsRemote(prefill?.remote ?? "") ?? "")
        : startKind === "mount"
          ? (prefill?.remote ?? "")
          : "",
    defaultBranch: prefill?.branch ?? "main",
    pathPrefix: "",
    token: "",
    installationId: initialInstallationId ?? "",
  });
  const urlRef = useRef<HTMLInputElement | null>(null);

  // The prefill's project arrives with the project list: preselect it once, never over a choice.
  const projectApplied = useRef(false);
  useEffect(() => {
    if (projectApplied.current || lockProject || !prefill || !projects) {
      return;
    }
    projectApplied.current = true;
    const match = resolvePrefillProject(prefill, projects);
    if (match) {
      setForm((current) => (current.projectId ? current : { ...current, projectId: match.id }));
    }
  }, [prefill, projects, lockProject]);

  // Autofocus in an effect — Biome forbids the `autoFocus` attribute.
  useEffect(() => {
    urlRef.current?.focus();
  }, []);

  const isMount = form.kind === "mount";
  const needsToken = form.kind === "pat";
  const needsInstallation = form.kind === "app";
  const tokenBlocked = needsToken && !secretsAvailable;
  const valid =
    form.remoteUrl.trim().length > 0 &&
    !tokenBlocked &&
    (!needsToken || form.token.trim().length > 0) &&
    (!needsInstallation || form.installationId.trim().length > 0);

  const submit = () => {
    if (!valid) {
      return;
    }
    create.mutate(
      {
        projectId: form.projectId || null,
        kind: form.kind,
        remoteUrl: form.remoteUrl.trim(),
        defaultBranch: form.defaultBranch.trim() || "main",
        pathPrefix: form.pathPrefix.trim(),
        ...(needsToken ? { token: form.token } : {}),
        ...(needsInstallation ? { installationId: form.installationId.trim() } : {}),
      },
      {
        onSuccess: (connection) => {
          onClose();
          toast.success(t("Connected {{name}}", { name: repositoryName(connection) }));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Connect a repository")}
      description={t(
        "History is read, never written: the server verifies with the provider that a credential cannot push before it keeps it.",
      )}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? t("Connecting…") : t("Connect")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {lockProject ? null : (
          <Select
            label={t("Belongs to")}
            fullWidth
            value={form.projectId}
            onChange={(event) => setForm({ ...form, projectId: event.target.value })}
          >
            <option value="">{t("The whole workspace")}</option>
            {(projects ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        )}

        <div className="flex flex-col gap-1">
          <Select
            label={t("How to reach it")}
            fullWidth
            value={form.kind}
            onChange={(event) =>
              setForm({ ...form, kind: event.target.value as GitConnectionKind })
            }
          >
            {OFFERED_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindLabel(kind, t)}
              </option>
            ))}
          </Select>
          <p className="text-small text-text-tertiary">{kindHint(form.kind, t)}</p>
        </div>

        {needsInstallation ? null : (
          <Input
            ref={urlRef}
            label={isMount ? t("Path inside the server container") : t("Repository URL")}
            placeholder={isMount ? "/repo" : "https://github.com/owner/repository"}
            fullWidth
            value={form.remoteUrl}
            onChange={(event) => setForm({ ...form, remoteUrl: event.target.value })}
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("Branch")}
            hint={isMount ? t("A mount reads whatever is checked out.") : undefined}
            value={form.defaultBranch}
            disabled={isMount}
            onChange={(event) => setForm({ ...form, defaultBranch: event.target.value })}
          />
          <Input
            label={t("Subtree (optional)")}
            placeholder="documentation/ai-map"
            hint={t("The folder this connection is. The menu starts there.")}
            value={form.pathPrefix}
            onChange={(event) => setForm({ ...form, pathPrefix: event.target.value })}
          />
        </div>

        {needsInstallation ? (
          <GitHubAppPicker
            installationId={form.installationId}
            remoteUrl={form.remoteUrl}
            prefill={prefill ?? null}
            onPick={(pick) => setForm((current) => ({ ...current, ...pick }))}
            onPublic={(url) =>
              setForm((current) => ({ ...current, kind: "public", remoteUrl: url }))
            }
          />
        ) : null}

        {needsToken ? (
          <Input
            type="password"
            label={t("Fine-grained token")}
            autoComplete="off"
            fullWidth
            disabled={tokenBlocked}
            error={
              tokenBlocked
                ? t(
                    "This host has no secret key configured, so it cannot keep a token. Use a mount, a public repository or a GitHub App.",
                  )
                : undefined
            }
            hint={
              tokenBlocked
                ? undefined
                : t("Contents: read and Metadata: read, one repository, with an expiry.")
            }
            value={form.token}
            onChange={(event) => setForm({ ...form, token: event.target.value })}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * The GitHub App half of the dialog: install the app on GitHub (the person picks their own
 * account and repositories there), then choose one of the installations this workspace PROVED
 * through GitHub, then one of its repositories. Nothing is typed, so nothing can be guessed.
 */
function GitHubAppPicker({
  installationId,
  remoteUrl,
  prefill,
  onPick,
  onPublic,
}: {
  installationId: string;
  remoteUrl: string;
  prefill: ConnectPrefill | null;
  onPick: (pick: { installationId: string; remoteUrl: string; defaultBranch?: string }) => void;
  onPublic: (url: string) => void;
}) {
  const { t } = useTranslation();
  const { data: status } = useGitHubAppStatus();
  const { data: installations, isLoading } = useGitHubInstallations(status?.available === true);
  const { data: repositories, isLoading: reposLoading } = useGitHubRepositories(installationId);
  const install = useStartGitHubInstall();
  const routerHref = useRouterState({ select: (state) => state.location.href });
  const coverage = usePrefillCoverage(prefill, installations, remoteUrl, onPick);
  const client = useQueryClient();
  const recheck = () => void client.invalidateQueries({ queryKey: ["git", "github"] });
  const publicUrl = prefill ? httpsRemote(prefill.remote) : null;
  const wanted = prefill ? (parseRemote(prefill.remote)?.fullName ?? prefill.remote) : "";

  if (status && !status.available) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-small text-danger">
          {t("The GitHub app is not configured on this host yet.")}
        </p>
        {publicUrl ? (
          <Button variant="neutral" className="self-start" onClick={() => onPublic(publicUrl)}>
            {t("Connect it as a public repository")}
          </Button>
        ) : null}
      </div>
    );
  }

  // The router's location, not window.location: inside the VS Code panel (memory history) the
  // window is the webview, and GitHub must come back to the desk page the person was on.
  const returnTo = () => withoutParam(routerHref.split("#")[0] ?? "", "githubInstall");
  const startInstall = () => install.mutate({ returnTo: returnTo() });
  // Already installed: GitHub would only open the installation's settings and never come back,
  // so ask it to confirm the person's existing installations instead.
  const linkExisting = () => install.mutate({ returnTo: returnTo(), link: true });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Select
            label={t("GitHub account")}
            fullWidth
            value={installationId}
            disabled={isLoading || !installations?.length}
            onChange={(event) => onPick({ installationId: event.target.value, remoteUrl: "" })}
          >
            <option value="">
              {installations?.length ? t("Choose an installation") : t("None installed yet")}
            </option>
            {(installations ?? []).map((i) => (
              <option key={i.installationId} value={i.installationId}>
                {i.account}
              </option>
            ))}
          </Select>
        </div>
        <Button variant="neutral" onClick={startInstall} disabled={install.isPending}>
          {t("Install on GitHub")}
        </Button>
      </div>
      <p className="text-small text-text-tertiary">
        {t(
          "Install the app on your own GitHub account or organisation and choose which repositories it may read. You come back here afterwards.",
        )}
      </p>
      <button
        type="button"
        onClick={linkExisting}
        disabled={install.isPending}
        className="self-start text-small text-[color:var(--link)] underline"
      >
        {t("Already installed on GitHub? Link it")}
      </button>
      {installationId ? (
        <Select
          label={t("Repository")}
          fullWidth
          value={remoteUrl}
          disabled={reposLoading}
          onChange={(event) => {
            const repo = repositories?.find((r) => r.url === event.target.value);
            onPick({
              installationId,
              remoteUrl: event.target.value,
              ...(repo ? { defaultBranch: repo.defaultBranch } : {}),
            });
          }}
        >
          <option value="">{reposLoading ? t("Loading") : t("Choose a repository")}</option>
          {(repositories ?? []).map((r) => (
            <option key={r.url} value={r.url}>
              {r.fullName}
              {r.private ? ` · ${t("private")}` : ""}
            </option>
          ))}
        </Select>
      ) : null}
      {prefill && coverage === "matched" ? (
        <p className="text-small text-text-secondary" role="status">
          {t("{{name}} is covered by this installation — it is preselected.", { name: wanted })}
        </p>
      ) : null}
      {prefill && coverage === "uncovered" ? (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-md border border-divider bg-bg-secondary p-3"
        >
          <p className="text-small text-text">
            {t("No GitHub App installation this workspace can use covers {{name}} yet.", {
              name: wanted,
            })}
          </p>
          <p className="text-small text-text-tertiary">
            {t(
              "Install the GitHub App on the account that owns it and allow this repository, then check again.",
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={startInstall} disabled={install.isPending}>
              {t("Install the GitHub App")}
            </Button>
            <Button variant="neutral" onClick={recheck}>
              {t("Check again")}
            </Button>
            {publicUrl ? (
              <Button variant="ghost" onClick={() => onPublic(publicUrl)}>
                {t("It is public — connect without the app")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** `href` (path + search) without one search param — whichever position it held. */
function withoutParam(href: string, name: string): string {
  const cut = href.indexOf("?");
  if (cut < 0) {
    return href;
  }
  const params = new URLSearchParams(href.slice(cut + 1));
  params.delete(name);
  const rest = params.toString();
  return `${href.slice(0, cut)}${rest ? `?${rest}` : ""}`;
}

type Coverage = "none" | "checking" | "matched" | "uncovered";

/**
 * With a prefill: read every installation's repositories (the same cache entries the picker
 * uses), preselect the first one that contains the remote, and say plainly when none does.
 * The GitHub install page opens OUTSIDE the app (a browser from VS Code), so coming back is
 * a window focus, not a navigation: refetch then.
 */
function usePrefillCoverage(
  prefill: ConnectPrefill | null,
  installations: { installationId: string }[] | undefined,
  remoteUrl: string,
  onPick: (pick: { installationId: string; remoteUrl: string; defaultBranch?: string }) => void,
): Coverage {
  const api = useApi();
  const active = Boolean(prefill && isGitHubRemote(prefill.remote));
  const results = useQueries({
    queries: (active ? (installations ?? []) : []).map((installation) => ({
      queryKey: ["git", "github", "repositories", installation.installationId],
      queryFn: (): Promise<GitHubRepository[]> =>
        api.git.github.repositories(installation.installationId),
      staleTime: 60_000,
    })),
  });
  const loading = !installations || results.some((r) => r.isLoading);
  const match =
    active && prefill
      ? findInstallationFor(
          prefill.remote,
          (installations ?? []).map((installation, index) => ({
            installationId: installation.installationId,
            repositories: results[index]?.data,
          })),
        )
      : null;

  const picked = useRef(false);
  useEffect(() => {
    if (!match || picked.current || remoteUrl) {
      return;
    }
    picked.current = true;
    onPick({
      installationId: match.installationId,
      remoteUrl: match.repository.url,
      defaultBranch: prefill?.branch ?? match.repository.defaultBranch,
    });
  }, [match, remoteUrl, onPick, prefill]);

  const client = useQueryClient();
  useEffect(() => {
    if (!active || match) {
      return;
    }
    const refresh = () => void client.invalidateQueries({ queryKey: ["git", "github"] });
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [active, match, client]);

  if (!active) {
    return "none";
  }
  if (match) {
    return "matched";
  }
  return loading ? "checking" : "uncovered";
}
